// src/components/MediaCarousel.js
import React, { useState, useEffect } from 'react';
import './MediaCarousel.css';

function MediaCarousel({ media = [], title = 'Media' }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isFullscreen) return;
      
      if (e.key === 'ArrowRight') {
        goToNext();
      } else if (e.key === 'ArrowLeft') {
        goToPrevious();
      } else if (e.key === 'Escape') {
        setIsFullscreen(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentIndex, isFullscreen, media.length]);
  
  const goToPrevious = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? media.length - 1 : prevIndex - 1
    );
  };
  
  const goToNext = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === media.length - 1 ? 0 : prevIndex + 1
    );
  };
  
  const goToSlide = (index) => {
    setCurrentIndex(index);
  };
  
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };
  
  if (!media || media.length === 0) {
    return (
      <div className="media-carousel-empty">
        <img 
          src="/placeholder.jpg" 
          alt="No media available" 
        />
      </div>
    );
  }
  
  const currentMedia = media[currentIndex];
  const isVideo = currentMedia.type === 'video';
  
  return (
    <>
      <div className="media-carousel">
        <div className="carousel-main">
          {media.length > 1 && (
            <button 
              className="carousel-nav carousel-nav-prev" 
              onClick={goToPrevious}
              aria-label="Previous media"
            >
              ‹
            </button>
          )}
          
          <div className="carousel-media-container" onClick={toggleFullscreen}>
            {isVideo ? (
              <video 
                src={currentMedia.url} 
                controls 
                className="carousel-media"
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <img 
                src={currentMedia.url} 
                alt={`${title} - ${currentIndex + 1}`}
                className="carousel-media"
              />
            )}
            
            <div className="fullscreen-hint">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            </div>
          </div>
          
          {media.length > 1 && (
            <button 
              className="carousel-nav carousel-nav-next" 
              onClick={goToNext}
              aria-label="Next media"
            >
              ›
            </button>
          )}
        </div>
        
        {media.length > 1 && (
          <div className="carousel-thumbnails">
            {media.map((item, index) => (
              <button
                key={index}
                className={`thumbnail ${index === currentIndex ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to media ${index + 1}`}
              >
                {item.type === 'video' ? (
                  <div className="video-thumbnail">
                    <video src={item.url} />
                    <div className="video-play-icon">▶</div>
                  </div>
                ) : (
                  <img src={item.url} alt={`Thumbnail ${index + 1}`} />
                )}
              </button>
            ))}
          </div>
        )}
        
        <div className="carousel-counter">
          {currentIndex + 1} / {media.length}
        </div>
      </div>
      
      {isFullscreen && (
        <div className="fullscreen-overlay" onClick={() => setIsFullscreen(false)}>
          <button 
            className="fullscreen-close" 
            onClick={() => setIsFullscreen(false)}
            aria-label="Close fullscreen"
          >
            ✕
          </button>
          
          {media.length > 1 && (
            <button 
              className="fullscreen-nav fullscreen-nav-prev" 
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              aria-label="Previous media"
            >
              ‹
            </button>
          )}
          
          <div className="fullscreen-content" onClick={(e) => e.stopPropagation()}>
            {isVideo ? (
              <video 
                src={currentMedia.url} 
                controls 
                className="fullscreen-media"
              />
            ) : (
              <img 
                src={currentMedia.url} 
                alt={`${title} - ${currentIndex + 1}`}
                className="fullscreen-media"
              />
            )}
          </div>
          
          {media.length > 1 && (
            <button 
              className="fullscreen-nav fullscreen-nav-next" 
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              aria-label="Next media"
            >
              ›
            </button>
          )}
          
          <div className="fullscreen-counter">
            {currentIndex + 1} / {media.length}
          </div>
        </div>
      )}
    </>
  );
}

export default MediaCarousel;
